import { Navigate, Route, Routes } from "react-router";
import { JWTTokenType } from "../stores/types/userAuthTypes.tsx";
import { PersonasListPage } from "../pages/personas/PersonasListPage.tsx";
import { PersonaGroupsPage } from "../pages/personas/PersonaGroupsPage.tsx";
import { SignInPage } from "../pages/auth/signIn/SignInPage.tsx";

import { NotFoundPage } from "../pages/notFound/NotFoundPage.tsx";
import { RevLayout } from "../components/layout/RevLayout.tsx";
import { DiscoverCommunitiesPage } from "../pages/discoverCommunities/DiscoverCommunitiesPage.tsx";
import { ManageCampaignsPage } from "../pages/campaigns/ManageCampaignsPage.tsx";
import { useAppDispatch, useAppSelector } from "../stores/hooks/hooks.ts";
import { SubscriptionSettingsPage } from "../pages/settings/subscription/SubscriptionSettingsPage.tsx";
import { SecuritySettingsPage } from "../pages/settings/security/SecuritySettingsPage.tsx";
import { ProfileSettingsWrapperPage } from "../pages/settings/profile/ProfileSettingsWrapperPage.tsx";
import { ContactUsWrapperPage } from "../pages/help/contactUs/ContactUsWrapperPage.tsx";
import { BrandDetailsWrapperPage } from "../pages/brand/BrandDetailsWrapperPage.tsx";
import { CreatorBriefsListPage } from "../pages/creatorBriefs/CreatorBriefsListPage.tsx";
import { CreatorBriefDetailPage } from "../pages/creatorBriefs/CreatorBriefDetailPage.tsx";
import { MediaPlanWrapperPage } from "../pages/mediaPlan/MediaPlanWrapperPage.tsx";
import { CompaniesPage } from "../pages/companies/CompaniesPage.tsx";
import { CompanySettingsWrapperPage } from "../pages/settings/companySettings/CompanySettingsWrapperPage.tsx";
import { CompanyPage } from "../pages/companies/company/CompanyPage.tsx";
import { ForgotPasswordPage } from "../pages/auth/forgotPassword/ForgotPasswordPage.tsx";
import { SetPasswordPage } from "../pages/auth/setPassword/SetPasswordPage.tsx";
import { UserCompleteRegisterPage } from "../pages/auth/userCompleteRegister/UserCompleteRegisterPage.tsx";
import { OnboardingPage } from "../pages/onboarding/OnboardingPage.tsx";
import { ManageCreatorsPage } from "../pages/creators/ManageCreatorsPage.tsx";
import { CompanyRouteLayout } from "../pages/companies/CompanyRouteLayout.tsx";
import { ProfileRolesType } from "../stores/types/profileTypes.tsx";
import { RevSpinner } from "@revmatics/catalyst";
import { useEffect, useMemo } from "react";
import { fetchOrganizationsList } from "../stores/thunks/organizationsThunk.tsx";
import { fetchCompanySettings } from "../stores/thunks/companyThunk.tsx";

type PropsType = {
  token: JWTTokenType;
};

const RequireRole = ({
  allowed,
  roles,
  isLoading,
  children,
}: {
  allowed: ProfileRolesType[];
  roles?: ProfileRolesType[];
  isLoading: boolean;
  children: React.ReactNode;
}) => {
  if (isLoading) {
    return (
      <div className="h-dvh">
        <RevSpinner size="xxl" color="primary" centered />
      </div>
    );
  }
  const hasAccess = roles?.some((r) => allowed.includes(r));

  if (!hasAccess) {
    return <Navigate to="/not-found" replace />;
  }

  return children;
};

export const AppRoutes = ({ token }: PropsType) => {
  const dispatch = useAppDispatch();

  const { brandById, brandList } = useAppSelector((state) => state.brand);
  const { organizationsList, isLoadingOrganizationsList } = useAppSelector(
    (state) => state.organizations,
  );
  const { companySettings, isLoadingSettings } = useAppSelector(
    (state) => state.company,
  );
  const authState = useAppSelector((state) => state.userAuth);

  useEffect(() => {
    if (!organizationsList && token) dispatch(fetchOrganizationsList());
    if (!companySettings && token) dispatch(fetchCompanySettings());
  }, [dispatch, organizationsList, token, companySettings]);

  const currentOrg = useMemo(() => {
    if (!(organizationsList && companySettings)) return null;
    return (
      organizationsList.find((org) => org.id === companySettings?.id) || null
    );
  }, [organizationsList, companySettings]);

  const isLoadingAdmin =
    isLoadingSettings ||
    isLoadingOrganizationsList ||
    !companySettings ||
    !organizationsList;

  const getAuthHomeRoute = () => {
    if (authState.enable_quickstart && !authState.has_completed_onboarding) {
      return "/onboarding";
    }
    if (token && brandList && brandList.length === 0) {
      return "/company-settings/brand/create";
    }
    return "/personas";
  };

  const baseRoutes = (
    <>
      <Route
        path="/signin"
        element={<Navigate replace to={getAuthHomeRoute()} />}
      />
      <Route
        path="/onboarding"
        element={
          <RevLayout isOnboardingLayout>
            <OnboardingPage />
          </RevLayout>
        }
      />
      <Route
        path="/company-settings"
        element={
          <RevLayout
            container
            title="Company Settings"
            crumbs={[
              { label: "Settings", pageId: "settings" },
              { label: "Company", pageId: "company_settings" },
            ]}
          >
            <CompanySettingsWrapperPage />
          </RevLayout>
        }
      />
      <Route
        path="/company-settings/brand/:brandId"
        element={
          <RevLayout
            container
            title={brandById?.name || "Brand"}
            crumbs={[
              { label: "Settings", pageId: "settings" },
              {
                label: "Brands",
                pageId: "brand_settings",
                href: "/company-settings",
              },
              { label: brandById?.name || "Brand", pageId: "brand" },
            ]}
            headerStick
          >
            <BrandDetailsWrapperPage />
          </RevLayout>
        }
      />
      <Route
        path="/company-settings/brand/create"
        element={
          <RevLayout
            container
            title="New Brand"
            crumbs={[
              { label: "Settings", pageId: "settings" },
              {
                label: "Brands",
                pageId: "brand_settings",
                href: "/company-settings",
              },
              { label: "New Brand", pageId: "newBrand" },
            ]}
          >
            <BrandDetailsWrapperPage />
          </RevLayout>
        }
      />
      <Route
        path="/subscription-settings"
        element={
          <RevLayout
            container
            title="Subscription"
            crumbs={[
              { label: "Settings", pageId: "settings" },
              { label: "Subscription", pageId: "subscription_settings" },
            ]}
          >
            <SubscriptionSettingsPage />
          </RevLayout>
        }
      />
      <Route
        path="/profile-settings"
        element={
          <RevLayout
            container
            title="Profile Settings"
            crumbs={[
              { label: "Settings", pageId: "settings" },
              { label: "Profile", pageId: "profile_settings" },
            ]}
          >
            <ProfileSettingsWrapperPage />
          </RevLayout>
        }
      />
      <Route
        path="/security-settings"
        element={
          <RevLayout
            container
            title="Security Settings"
            crumbs={[
              { label: "Settings", pageId: "settings" },
              { label: "Security", pageId: "security_settings" },
            ]}
          >
            <SecuritySettingsPage />
          </RevLayout>
        }
      />
      <Route
        path="/contact-us"
        element={
          <RevLayout
            container
            title="Contact Us"
            crumbs={[
              { label: "Help", pageId: "help" },
              { label: "Contact Us", pageId: "contact_us" },
            ]}
          >
            <ContactUsWrapperPage />
          </RevLayout>
        }
      />
      <Route
        path="companies"
        element={
          <RequireRole
            allowed={["INTERNAL_CUSTOMER_ONBOARDING"]}
            isLoading={isLoadingAdmin}
            roles={currentOrg?.groups}
          >
            <CompanyRouteLayout />
          </RequireRole>
        }
      >
        <Route index element={<CompaniesPage />} />
      </Route>
      <Route
        path="companies/:companyId"
        element={
          <RequireRole
            allowed={["INTERNAL_CUSTOMER_ONBOARDING"]}
            isLoading={isLoadingAdmin}
            roles={currentOrg?.groups}
          >
            <CompanyRouteLayout />
          </RequireRole>
        }
      >
        <Route index element={<CompanyPage />} />
      </Route>
      <Route
        path="companies/:companyId/brand/:brandId"
        element={
          <RequireRole
            allowed={["INTERNAL_CUSTOMER_ONBOARDING"]}
            isLoading={isLoadingAdmin}
            roles={currentOrg?.groups}
          >
            <CompanyRouteLayout />
          </RequireRole>
        }
      >
        <Route index element={<BrandDetailsWrapperPage />} />
      </Route>
      <Route
        path="companies/:companyId/brand/create"
        element={
          <RequireRole
            allowed={["INTERNAL_CUSTOMER_ONBOARDING"]}
            isLoading={isLoadingAdmin}
            roles={currentOrg?.groups}
          >
            <CompanyRouteLayout />
          </RequireRole>
        }
      >
        <Route index element={<BrandDetailsWrapperPage />} />
      </Route>
      <Route
        path="/register/complete"
        element={
          <RevLayout isAuthLayout={true} title="Complete Register">
            <UserCompleteRegisterPage logout={true} />
          </RevLayout>
        }
      />
      <Route path="*" element={<Navigate replace to="/not-found" />} />
      <Route
        path={"/not-found"}
        element={
          <RevLayout container initSideBarOpen={false}>
            <NotFoundPage />
          </RevLayout>
        }
      />
    </>
  );

  if (token && brandList && brandList.length === 0) {
    return (
      <Routes>
        <Route
          index
          element={<Navigate replace to="/company-settings/brand/create" />}
        />
        {baseRoutes}
      </Routes>
    );
  }
  if (token) {
    return (
      <Routes>
        <Route index element={<Navigate replace to="/personas" />} />
        <Route
          path="/generations"
          element={
            <RevLayout
              container
              title="Personas Groups"
              crumbs={[
                { label: "Audiences", pageId: "audiences" },
                { label: "Generations", pageId: "generations" },
              ]}
              tableExpand
            >
              <PersonaGroupsPage />
            </RevLayout>
          }
        />
        <Route
          path="/personas"
          element={
            <RevLayout
              container
              title="Personas List"
              crumbs={[
                { label: "Audiences", pageId: "audiences" },
                { label: "Personas", pageId: "personas" },
              ]}
              tableExpand
            >
              <PersonasListPage />
            </RevLayout>
          }
        />
        <Route
          path="/discover-communities"
          element={
            <RevLayout
              container
              title="Discover Communities"
              crumbs={[
                {
                  label: "Buyer Personas",
                  href: "/personas",
                  pageId: "personas",
                },
                {
                  label: "Discover Communities",
                  pageId: "discover_communities",
                },
              ]}
            >
              <DiscoverCommunitiesPage />
            </RevLayout>
          }
        />
        <Route
          path="/creators"
          element={
            <RevLayout
              container
              title="Manage Creators"
              tableExpand
              crumbs={[
                {
                  label: "Manage Creators",
                  pageId: "creators_communities",
                },
              ]}
            >
              <ManageCreatorsPage />
            </RevLayout>
          }
        />
        <Route
          path="/campaigns"
          element={
            <RevLayout
              container
              title="Manage Campaigns"
              crumbs={[{ label: "Manage Campaigns", pageId: "campaigns" }]}
              tableExpand
            >
              <ManageCampaignsPage />
            </RevLayout>
          }
        />
        <Route
          path="/campaigns/:campaignId/media-plan/"
          element={
            <RevLayout
              container
              title="Media Plan"
              crumbs={[
                {
                  label: "Campaigns",
                  pageId: "campaigns",
                },
                {
                  label: "Manage Campaigns",
                  pageId: "manage-campaigns",
                  href: "/campaigns",
                },
                { label: "Media Plan", pageId: "media-plan" },
              ]}
            >
              <MediaPlanWrapperPage />
            </RevLayout>
          }
        />
        <Route
          path="/creator-briefs"
          element={
            <RevLayout
              container
              title="Creator Briefs"
              crumbs={[
                {
                  label: "Campaigns",
                  href: "/campaigns",
                  pageId: "campaigns",
                },
                { label: "Creator Briefs", pageId: "creator_briefs" },
              ]}
              tableExpand
            >
              <CreatorBriefsListPage />
            </RevLayout>
          }
        />
        <Route
          path="/creator-briefs/:creatorBriefId"
          element={
            <RevLayout
              container
              title="Creator Brief Details"
              crumbs={[
                {
                  label: "Campaigns",
                  pageId: "campaigns",
                  href: "/campaigns",
                },
                {
                  label: "Creator Briefs",
                  pageId: "creator-briefs",
                  href: "/creator-briefs",
                },
                {
                  label: "Creator Brief Details",
                  pageId: "creator-brief-details",
                },
              ]}
            >
              <CreatorBriefDetailPage />
            </RevLayout>
          }
        />
        {baseRoutes}
      </Routes>
    );
  }
  return (
    <Routes>
      <Route index element={<Navigate replace to="/signin" />} />
      <Route
        path="/signin"
        element={
          <RevLayout isAuthLayout={true} title="Sign In">
            <SignInPage />
          </RevLayout>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <RevLayout isAuthLayout={true} title="Forgot Password">
            <ForgotPasswordPage />
          </RevLayout>
        }
      />
      <Route
        path="/set-password"
        element={
          <RevLayout isAuthLayout={true} title="Set Password">
            <SetPasswordPage />
          </RevLayout>
        }
      />
      <Route
        path="/register/complete"
        element={
          <RevLayout isAuthLayout={true} title="Complete Register">
            <UserCompleteRegisterPage />
          </RevLayout>
        }
      />
      <Route path="*" element={<Navigate replace to="/signin" />} />
    </Routes>
  );
};
